import { DeleteOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Input, message, Modal, Popconfirm, Space } from "antd";
import { useEffect, useRef, useState } from "react";

interface IMappingConfiguration {
    configurationId: string;
    title: string;
    mappingConfiguration: any;
    createdAt?: string;
    updatedAt?: string;
}

interface MappingConfigurationManagerProps {
    businessAreaId: string;
    schemaId: string;
    currentMapping: IMappingSchemaFields | undefined;
    mappingConfigurations: {
        Items: IMappingConfiguration[];
    };
    onSaveMappingConfiguration: (businessAreaId: string, schemaId: string, title: string, mappingConfiguration: any) => void;
    onGetMappingConfigurations: (businessAreaId: string, schemaId: string) => void;
    onDeleteMappingConfiguration: (businessAreaId: string, schemaId: string, configurationId: string) => void;
    onLoadMappingConfiguration: (mappingConfiguration: any) => void;
    saveMappingConfigurationResult?: { success: boolean; error?: any };
    getMappingConfigurationsResult?: { success: boolean; error?: any };
    deleteMappingConfigurationResult?: { success: boolean; error?: any };
}

export default function MappingConfigurationManager({
    businessAreaId,
    schemaId,
    currentMapping,
    mappingConfigurations,
    onSaveMappingConfiguration,
    onGetMappingConfigurations,
    onDeleteMappingConfiguration,
    onLoadMappingConfiguration,
    saveMappingConfigurationResult,
    getMappingConfigurationsResult,
    deleteMappingConfigurationResult,
}: MappingConfigurationManagerProps) {
    const [isSaveModalVisible, setIsSaveModalVisible] = useState(false);
    const [configurationTitle, setConfigurationTitle] = useState("");
    const [loading, setLoading] = useState(false);
    const skipInitialSaveResult = useRef(true);
    const skipInitialDeleteResult = useRef(true);
    const isUpdating = useRef(false);
    const pendingSave = useRef<{ title: string; mapping: any } | null>(null);

    useEffect(() => {
        if (businessAreaId && schemaId) {
            onGetMappingConfigurations(businessAreaId, schemaId);
        }
    }, [businessAreaId, schemaId, onGetMappingConfigurations]);

    useEffect(() => {
        if (!saveMappingConfigurationResult) return;
        if (skipInitialSaveResult.current) {
            skipInitialSaveResult.current = false;
            return;
        }

        if (saveMappingConfigurationResult.success) {
            if (isUpdating.current) {
                message.success("Mapping configuration updated successfully");
                isUpdating.current = false;
            } else {
                message.success("Mapping configuration saved successfully");
            }
            setIsSaveModalVisible(false);
            setConfigurationTitle("");
            onGetMappingConfigurations(businessAreaId, schemaId);
        } else if (saveMappingConfigurationResult.error) {
            message.error("Failed to save mapping configuration");
            isUpdating.current = false;
        }
        setLoading(false);
    }, [saveMappingConfigurationResult, businessAreaId, schemaId, onGetMappingConfigurations]);

    useEffect(() => {
        if (!deleteMappingConfigurationResult) return;
        if (skipInitialDeleteResult.current) {
            skipInitialDeleteResult.current = false;
            return;
        }

        if (deleteMappingConfigurationResult.success) {
            if (!isUpdating.current) {
                message.success("Mapping configuration deleted successfully");
            } else if (pendingSave.current) {
                // If updating, now proceed to save
                onSaveMappingConfiguration(businessAreaId, schemaId, pendingSave.current.title, pendingSave.current.mapping);
                pendingSave.current = null;
            }
            onGetMappingConfigurations(businessAreaId, schemaId);
        } else if (deleteMappingConfigurationResult.error) {
            message.error("Failed to delete mapping configuration");
            isUpdating.current = false;
            pendingSave.current = null;
            setLoading(false);
        }
    }, [deleteMappingConfigurationResult, businessAreaId, schemaId, onGetMappingConfigurations, onSaveMappingConfiguration]);

    const handleSave = () => {
        if (!configurationTitle.trim()) {
            message.warning("Please enter a title for the mapping configuration");
            return;
        }
        if (!currentMapping) {
            message.warning("No mapping configuration to save");
            return;
        }

        const existingConfig = mappingConfigurations?.Items?.find((config) => config.title.toLowerCase() === configurationTitle.trim().toLowerCase());

        if (existingConfig) {
            Modal.confirm({
                title: "Configuration already exists",
                content: `A configuration with the name "${existingConfig.title}" already exists. Do you want to overwrite it?`,
                okText: "Overwrite",
                cancelText: "Cancel",
                onOk: () => {
                    setLoading(true);
                    isUpdating.current = true;
                    // Store the pending save data
                    pendingSave.current = {
                        title: configurationTitle.trim(),
                        mapping: currentMapping,
                    };
                    // Start the update process by deleting the existing one
                    onDeleteMappingConfiguration(businessAreaId, schemaId, existingConfig.configurationId);
                },
            });
            return;
        }

        setLoading(true);
        onSaveMappingConfiguration(businessAreaId, schemaId, configurationTitle.trim(), currentMapping);
    };

    const handleLoadConfiguration = (config: IMappingConfiguration) => {
        if (config.mappingConfiguration) {
            onLoadMappingConfiguration(config.mappingConfiguration);
            message.success(`Loaded mapping configuration: ${config.title}`);
        }
    };

    const handleDeleteConfiguration = (configurationId: string, title: string) => {
        onDeleteMappingConfiguration(businessAreaId, schemaId, configurationId);
    };

    const menuItems: MenuProps["items"] =
        mappingConfigurations?.Items?.map((config) => ({
            key: config.configurationId,
            label: (
                <div
                    className="flex min-w-[300px] cursor-pointer items-center justify-between gap-4"
                    onClick={() => handleLoadConfiguration(config)}
                >
                    <span className="flex-1 truncate" title={config.title}>
                        {config.title}
                    </span>
                    <Space>
                        <Button
                            type="text"
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLoadConfiguration(config);
                            }}
                            title="Load this configuration"
                        >
                            Load
                        </Button>
                        <Popconfirm
                            title={`Delete mapping configuration "${config.title}"?`}
                            description="This action cannot be undone."
                            onConfirm={(e) => {
                                e?.stopPropagation();
                                handleDeleteConfiguration(config.configurationId, config.title);
                            }}
                            onCancel={(e) => e?.stopPropagation()}
                            okText="Yes"
                            cancelText="No"
                        >
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={(e) => e.stopPropagation()} title="Delete this configuration">
                                Delete
                            </Button>
                        </Popconfirm>
                    </Space>
                </div>
            ),
        })) || [];

    if (mappingConfigurations?.Items?.length === 0) {
        menuItems.push({
            key: "no-configs",
            label: <span className="italic text-gray-400">No saved configurations</span>,
            disabled: true,
        });
    }

    const hasConfigurations = mappingConfigurations?.Items && mappingConfigurations.Items.length > 0;

    return (
        <div className="flex items-center gap-2">
            <Button type="default" icon={<SaveOutlined />} onClick={() => setIsSaveModalVisible(true)} disabled={!currentMapping} title="Save current mapping configuration">
                Save Mapping
            </Button>

            <Dropdown menu={{ items: menuItems }} trigger={["click"]} disabled={!hasConfigurations} placement="bottomRight">
                <Button type="default" disabled={!hasConfigurations} title={hasConfigurations ? "Load a saved mapping configuration" : "No saved configurations"}>
                    Load Mapping {hasConfigurations && `(${mappingConfigurations.Items.length})`}
                </Button>
            </Dropdown>

            <Modal
                title="Save Mapping Configuration"
                open={isSaveModalVisible}
                onOk={handleSave}
                onCancel={() => {
                    setIsSaveModalVisible(false);
                    setConfigurationTitle("");
                }}
                confirmLoading={loading}
                okText="Save"
                cancelText="Cancel"
            >
                <div className="py-4">
                    <label className="mb-2 block text-sm font-medium">Configuration Title</label>
                    <Input
                        placeholder="Enter a title for this mapping configuration"
                        value={configurationTitle}
                        onChange={(e) => setConfigurationTitle(e.target.value)}
                        onPressEnter={handleSave}
                        autoFocus
                    />
                    <p className="mt-2 text-xs text-gray-500">This will save your current field mappings for easy reuse later.</p>
                </div>
            </Modal>
        </div>
    );
}
